import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isPendingPreparationsError, pendingPreparations } from '../../../domain/menage/preparations';
import type { ChecklistTemplate, HousekeepingRoomStatus } from '../../../pages/housekeeping/types/housekeeping';
import { useTerminerAvecChecklist, useTerminerMenage } from '../../api/useActionsMenage';
import { t } from '../../i18n/langue';

/**
 * Terminer le ménage — les deux garde-fous de `CleaningPage` du web, dans le même
 * ordre : les préparations du jour d'abord (le serveur les vérifie aussi), puis les
 * points obligatoires de la checklist (le serveur, lui, ne les vérifie PAS : sans
 * ce contrôle, une checklist requise serait contournée en silence).
 *
 * Aucune requête ne part tant qu'un garde-fou tient. Le succès ramène à la liste,
 * qui annonce le résultat en `role=status`.
 */
const PREPARATIONS_EN_ATTENTE = 'menage.fin.preparationsEnAttente';

export function useFinDuMenage(chambre: HousekeepingRoomStatus | undefined, templates: ChecklistTemplate[]) {
    const navigate = useNavigate();
    const avecChecklist = useTerminerAvecChecklist();
    const simple = useTerminerMenage();
    const [coches, setCoches] = useState<Set<number>>(() => new Set());
    const [notes, setNotes] = useState('');
    const [manquant, setManquant] = useState<number | null>(null);
    const [alerte, setAlerte] = useState<string | null>(null);

    const basculer = (itemId: number) => {
        setCoches((avant) => {
            const apres = new Set(avant);
            if (apres.has(itemId)) apres.delete(itemId);
            else apres.add(itemId);
            return apres;
        });
        if (itemId === manquant) setManquant(null);
    };

    const terminer = () => {
        if (!chambre) return;
        setAlerte(null);

        if (pendingPreparations(chambre).length > 0) {
            setAlerte(t(PREPARATIONS_EN_ATTENTE));
            return;
        }

        const rappels = {
            onSuccess: () =>
                navigate('/housekeeping', { state: { message: t('menage.fin.termine', { chambre: chambre.room_name }) } }),
            onError: (erreur: unknown) =>
                setAlerte(
                    isPendingPreparationsError(erreur)
                        ? t(PREPARATIONS_EN_ATTENTE)
                        : t('menage.fin.echec'),
                ),
        };

        const items = templates.flatMap((template) => template.items ?? []);
        if (items.length === 0) {
            simple.mutate({ serviceId: chambre.service_id }, rappels);
            return;
        }

        const premierManquant = items.find((item) => item.is_required && !coches.has(item.id));
        if (premierManquant) {
            setManquant(premierManquant.id);
            setAlerte(t('menage.fin.pointObligatoire', { point: premierManquant.label }));
            return;
        }

        avecChecklist.mutate(
            {
                serviceId: chambre.service_id,
                checklists: templates.map((template) => ({
                    template_id: template.id,
                    completed_items: (template.items ?? []).filter((i) => coches.has(i.id)).map((i) => i.id),
                })),
                ...(notes.trim() ? { notes: notes.trim() } : {}),
            },
            rappels,
        );
    };

    return {
        coches,
        basculer,
        notes,
        setNotes,
        manquant,
        alerte,
        terminer,
        enCours: avecChecklist.isLoading || simple.isLoading,
    };
}
